/**
 * Interpreter: ground rules against relation data into a concrete LP — step 3.
 *
 * The rules are the intensional form; this walks them against actual rows to
 * produce the extensional model the solver consumes. Two nested pieces:
 *   - a quantifier list is a conjunctive query, evaluated over the data into a
 *     stream of bindings (one row per bound variable, joined by `where`);
 *   - an expression is evaluated into a linear form over the decision variables.
 *
 * From those, three kinds of grounding:
 *   - {@link groundRule} — a comparison body per binding → constraint rows;
 *   - {@link groundVariables} — a ranged body per binding → a variable + bounds
 *     (the quantifiers are the index space, the range is the bounds);
 *   - {@link groundObjective} — one linear expression → the objective.
 * {@link groundModel} assembles all three into a {@link Model}.
 *
 * ## Data
 *
 * {@link Data} holds rows per relation. For an entity table a row is its columns.
 * For an applied relation the single value column holds a **number** (a `data`
 * relation like `demand`) or the **LP variable name** (a `decision` relation like
 * `flow`). A decision variable with no row (e.g. `theta` at the slack bus)
 * grounds to 0, which is how the slack angle drops out of the DC-flow equation
 * and how the slack bus gets no `theta` bound.
 */

import { Constraint, Variable, ObjectiveFunction, Model, IndexValue } from '../highs';
import { Catalog } from './schema';
import { Rule, Objective, Expr, Pred, Quantifier } from './ruleAst';
import { instanceName, formatIndexValue } from './naming';

/** A row of a relation: column name → value (or, for a decision cell, its LP variable name). */
export type Row = Record<string, IndexValue>;

/** Rows per relation. */
export type Data = Record<string, Row[]>;

export interface Binding {
  relation: string;
  row: Row;
}
export type Env = Map<string, Binding>;

/** A linear form: variable coefficients plus a constant. */
interface Linear {
  coeffs: Map<string, number>;
  constant: number;
}

const constLinear = (c: number): Linear => ({ coeffs: new Map(), constant: c });

function combine(a: Linear, b: Linear, sign: number): Linear {
  const coeffs = new Map(a.coeffs);
  for (const [k, v] of b.coeffs) coeffs.set(k, (coeffs.get(k) ?? 0) + sign * v);
  return { coeffs, constant: a.constant + sign * b.constant };
}

function scale(a: Linear, s: number): Linear {
  const coeffs = new Map<string, number>();
  for (const [k, v] of a.coeffs) coeffs.set(k, v * s);
  return { coeffs, constant: a.constant * s };
}

const isConst = (a: Linear): boolean => a.coeffs.size === 0;

/** The query + naming layer of grounding — the part independent of the backend. */
export interface Grounder {
  rows(relation: string): Row[];
  bindings(quantifiers: Quantifier[], base: Env): Env[];
  truthy(p: Pred, env: Env): boolean;
  keyValues(e: Expr, env: Env): IndexValue[];
  numeric(e: Expr, env: Env): number;
  lookup(relation: string, key: IndexValue[]): Row | undefined;
  nameForApply(e: { relation: string; args: { value: Expr }[] }, env: Env): string;
}

/**
 * The query + naming layer of grounding, closed over a dataset and catalog:
 * everything independent of the solver backend — the conjunctive-query
 * `bindings`, predicate and index/scalar evaluation (`truthy`/`keyValues`/
 * `numeric`), data `lookup`, and the variable name an applied decision reference
 * resolves to (`nameForApply`). The linear lowering below and the nonlinear
 * residual lowering (../nonlinear) both build on it — the front half of the
 * pipeline is shared; only the lowering target differs.
 */
export function makeGrounder(data: Data, catalog: Catalog): Grounder {
  const rows = (relation: string): Row[] => data[relation] ?? [];

  /** The answers to a conjunctive query — the quantifiers joined over the data. */
  function bindings(quantifiers: Quantifier[], base: Env): Env[] {
    let envs: Env[] = [base];
    for (const q of quantifiers) {
      const next: Env[] = [];
      for (const env of envs) {
        for (const row of rows(q.relation)) {
          const extended: Env = new Map(env);
          extended.set(q.var, { relation: q.relation, row });
          if (!q.where || truthy(q.where, extended)) next.push(extended);
        }
      }
      envs = next;
    }
    return envs;
  }

  function truthy(p: Pred, env: Env): boolean {
    if (p.kind === 'and') return truthy(p.left, env) && truthy(p.right, env);
    const [a] = keyValues(p.left, env);
    const [b] = keyValues(p.right, env);
    switch (p.op) {
      case '==':
        return a === b;
      case '!=':
        return a !== b;
      case '<':
        return (a as number) < (b as number);
      case '<=':
        return (a as number) <= (b as number);
      case '>':
        return (a as number) > (b as number);
      case '>=':
        return (a as number) >= (b as number);
    }
  }

  /** The key value(s) an expression contributes in an index position (a row spreads). */
  function keyValues(e: Expr, env: Env): IndexValue[] {
    if (e.kind === 'ref') {
      const b = env.get(e.name)!;
      return catalog[b.relation].keys.map((k) => b.row[k]);
    }
    if (e.kind === 'attr') return [env.get(e.obj)!.row[e.col]];
    if (e.kind === 'num' || e.kind === 'str') return [e.value];
    return [numeric(e, env)];
  }

  /** Evaluate a purely numeric expression (index arithmetic, a scalar column, sqrt). */
  function numeric(e: Expr, env: Env): number {
    switch (e.kind) {
      case 'num':
        return e.value;
      case 'ref': {
        const b = env.get(e.name)!;
        return Number(b.row[catalog[b.relation].keys[0]]);
      }
      case 'attr':
        return Number(env.get(e.obj)!.row[e.col]);
      case 'call': {
        const args = e.args.map((a) => numeric(a, env));
        if (e.fn === 'sqrt') return Math.sqrt(args[0]);
        if (e.fn === 'min') return Math.min(...args);
        if (e.fn === 'max') return Math.max(...args);
        if (e.fn === 'sin') return Math.sin(args[0]);
        if (e.fn === 'cos') return Math.cos(args[0]);
        return NaN;
      }
      case 'neg':
        return -numeric(e.operand, env);
      case 'binary': {
        const l = numeric(e.left, env);
        const r = numeric(e.right, env);
        switch (e.op) {
          case '+':
            return l + r;
          case '-':
            return l - r;
          case '*':
            return l * r;
          case '/':
            return l / r;
          case '^':
            return Math.pow(l, r);
        }
      }
      case 'apply': {
        // A data lookup is a number like any other — `min(g.max, wind_strength(t))`
        // reads one. Only the solver-assigned relations have no value yet.
        const schema = catalog[e.relation];
        if (!schema || schema.kind !== 'data') {
          throw new Error(`'${e.relation}' has no value here; it isn't a data relation`);
        }
        const cell = lookup(e.relation, e.args.flatMap((a) => keyValues(a.value, env)));
        return cell ? Number(cell[schema.values[0]]) : 0;
      }
      case 'cond':
        // The test reads bound rows only, so which branch applies is settled
        // here; the other one is never evaluated.
        return numeric(truthy(e.when, env) ? e.then : e.otherwise, env);
      case 'str':
        throw new Error(`a string ("${e.value}") has no numeric value`);
      default:
        throw new Error(`non-numeric expression in index/scalar position: ${e.kind}`);
    }
  }

  function lookup(relation: string, key: IndexValue[]): Row | undefined {
    const keys = catalog[relation].keys;
    return rows(relation).find((row) => keys.every((k, i) => row[k] === key[i]));
  }

  /**
   * The LP variable name an applied decision reference resolves to. Computed
   * from the relation and its argument values through the shared naming — the
   * same call the variable rules use — so a constraint and its variable always
   * agree on the name.
   */
  function nameForApply(e: { relation: string; args: { value: Expr }[] }, env: Env): string {
    const keys = catalog[e.relation].keys;
    const vals = e.args.flatMap((a) => keyValues(a.value, env));
    return instanceName(
      e.relation,
      vals.map((v, i) => formatIndexValue(keys[i], v)),
    );
  }

  return { rows, bindings, truthy, keyValues, numeric, lookup, nameForApply };
}

/**
 * The linear evaluator: the shared grounder plus the linear lowering
 * (`linear`/`constant`), closed over the decision-variable universe `exists` an
 * applied reference is resolved against.
 */
function makeEvaluator(data: Data, catalog: Catalog, exists: Set<string>) {
  const { bindings, truthy, keyValues, numeric, lookup, nameForApply } = makeGrounder(
    data,
    catalog,
  );

  /** Evaluate a body/objective expression into a linear form. */
  function linear(e: Expr, env: Env): Linear {
    switch (e.kind) {
      case 'num':
      case 'str':
      case 'ref':
      case 'attr':
      case 'call':
        return constLinear(numeric(e, env));
      case 'neg':
        return scale(linear(e.operand, env), -1);
      case 'binary': {
        const l = linear(e.left, env);
        const r = linear(e.right, env);
        if (e.op === '+') return combine(l, r, 1);
        if (e.op === '-') return combine(l, r, -1);
        if (e.op === '*') {
          if (isConst(l)) return scale(r, l.constant);
          if (isConst(r)) return scale(l, r.constant);
          throw new Error('non-linear product of two variable expressions');
        }
        if (e.op === '^') {
          // A power is linear only when it's a constant folded from constants.
          if (isConst(l) && isConst(r)) return constLinear(Math.pow(l.constant, r.constant));
          throw new Error('non-linear power of a variable expression');
        }
        // '/'
        if (isConst(r)) return scale(l, 1 / r.constant);
        throw new Error('division by a variable expression');
      }
      case 'apply': {
        const schema = catalog[e.relation];
        if (schema.kind === 'decision') {
          // Resolve to the variable's name; if it isn't in the grounded universe
          // (e.g. slack-bus theta, excluded by the theta rule's `where`) ⇒ 0.
          const name = nameForApply(e, env);
          return exists.has(name) ? { coeffs: new Map([[name, 1]]), constant: 0 } : constLinear(0);
        }
        // Data relation (e.g. demand): look up the value.
        const key = e.args.flatMap((a) => keyValues(a.value, env));
        const cell = lookup(e.relation, key);
        return constLinear(cell ? Number(cell[schema.values[0]]) : 0);
      }
      case 'sum': {
        let acc = constLinear(0);
        for (const senv of bindings(e.over, env)) acc = combine(acc, linear(e.body, senv), 1);
        return acc;
      }
      case 'cond':
        // Grounding picks the branch, so only one of them contributes to the
        // row — the model stays linear however many cases there are.
        return linear(truthy(e.when, env) ? e.then : e.otherwise, env);
    }
  }

  /** A constant expression's value (bounds, RHS); errors if it references a variable. */
  function constant(e: Expr, env: Env): number {
    const l = linear(e, env);
    if (l.coeffs.size > 0) throw new Error('expected a constant expression, got a variable');
    return l.constant;
  }

  return { bindings, linear, constant, nameForApply };
}

/** The names of the decision variables produced by grounding the variable rules. */
function existingVariables(variables: Variable[]): Set<string> {
  return new Set(variables.map((v) => v.name));
}

interface BoundIndex {
  var: string;
  relation: string;
  value: IndexValue;
}

/** The key value of each top-level quantifier's bound row, with its relation. */
function boundIndices(quantifiers: Quantifier[], env: Env, catalog: Catalog): BoundIndex[] {
  return quantifiers.map((q) => {
    const keys = catalog[q.relation].keys;
    const row = env.get(q.var)!.row;
    const value = keys.length === 1 ? row[keys[0]] : keys.map((k) => row[k]).join(':');
    return { var: q.var, relation: q.relation, value };
  });
}

/** Structured `indices` metadata (raw values, keyed by the quantifier's relation). */
const indicesOf = (bound: BoundIndex[]): Record<string, IndexValue> =>
  Object.fromEntries(bound.map((b) => [b.relation, b.value]));

/** The instance name for a grounded object: qualifier + the rendered index values. */
const nameOf = (qualifier: string, bound: BoundIndex[]): string =>
  instanceName(qualifier, bound.map((b) => formatIndexValue(b.relation, b.value)));

/**
 * Ground one comparison rule into constraint rows. `exists` is the set of
 * decision-variable names in play; an applied reference outside it grounds to 0.
 */
export function groundRule(rule: Rule, data: Data, catalog: Catalog, exists: Set<string>): Constraint[] {
  const { bindings, linear } = makeEvaluator(data, catalog, exists);
  const constraints: Constraint[] = [];
  for (const env of bindings(rule.quantifiers, new Map())) {
    const body = rule.body;
    // Ranged bodies are variable bounds and definitions are derived data; only a
    // comparison grounds into a constraint row.
    if (body.kind !== 'cmp') continue;

    const combined = combine(linear(body.left, env), linear(body.right, env), -1);
    const coefficients = new Map<string, number>();
    for (const [k, v] of combined.coeffs) if (v !== 0) coefficients.set(k, v);

    const bound = boundIndices(rule.quantifiers, env, catalog);
    constraints.push({
      name: nameOf(rule.name, bound),
      coefficients,
      sense: body.op,
      rhs: -combined.constant || 0, // constant term moves right (normalize -0)
      group: rule.name,
      indices: indicesOf(bound),
    });
  }
  return constraints;
}

/**
 * Ground definition rules into data rows, extending `data` with what they
 * compute. Each rule contributes one row per binding of its parameters: the key
 * columns from the bound rows, the value from its body.
 *
 * Rules are applied in order and each sees everything produced before it, so a
 * derived relation can be built on another — but only on one defined earlier,
 * since nothing here iterates to a fixed point.
 */
export function groundDerived(rules: Rule[], data: Data, catalog: Catalog): Data {
  const out: Data = { ...data };
  for (const rule of rules) {
    const body = rule.body;
    if (body.kind !== 'define') continue;
    const schema = catalog[rule.name];
    if (!schema) continue;

    // Built against the data so far, so earlier definitions are visible.
    const { bindings, constant } = makeEvaluator(out, catalog, new Set());
    // A definition owns its relation outright: these rows *are* the relation,
    // never an addition to rows already under that name.
    const rows: Row[] = [];
    for (const env of bindings(rule.quantifiers, new Map())) {
      const keys = boundIndices(rule.quantifiers, env, catalog);
      const row: Row = Object.fromEntries(schema.keys.map((key, i) => [key, keys[i]?.value]));
      row[schema.values[0]] = constant(body.expr, env);
      rows.push(row);
    }
    out[rule.name] = rows;
  }
  return out;
}

/** Ground several comparison rules, concatenating their constraints. */
export function groundRules(rules: Rule[], data: Data, catalog: Catalog, exists: Set<string>): Constraint[] {
  return rules.flatMap((rule) => groundRule(rule, data, catalog, exists));
}

/**
 * Ground ranged rules into decision variables with bounds. The quantifiers are
 * the variable's index space (so a `where` filter — e.g. excluding the slack bus
 * from `theta` — is what decides which variables exist), the range subject is the
 * variable being declared (named via the shared naming), and lower/upper are its
 * bounds.
 */
export function groundVariables(rules: Rule[], data: Data, catalog: Catalog): Variable[] {
  const { bindings, constant, nameForApply } = makeEvaluator(data, catalog, new Set());
  const variables: Variable[] = [];
  for (const rule of rules) {
    const body = rule.body;
    if (body.kind !== 'range' || body.expr.kind !== 'apply') continue;
    const subject = body.expr;
    for (const env of bindings(rule.quantifiers, new Map())) {
      variables.push({
        name: nameForApply(subject, env),
        lowerBound: constant(body.lower, env),
        upperBound: constant(body.upper, env),
        group: subject.relation,
        indices: indicesOf(boundIndices(rule.quantifiers, env, catalog)),
      });
    }
  }
  return variables;
}

/** Ground the objective: evaluate its expression into coefficients. */
export function groundObjective(
  obj: Objective,
  data: Data,
  catalog: Catalog,
  exists: Set<string>,
): ObjectiveFunction {
  const { linear } = makeEvaluator(data, catalog, exists);
  const le = linear(obj.expr, new Map());
  const coefficients = new Map<string, number>();
  for (const [k, v] of le.coeffs) if (v !== 0) coefficients.set(k, v);
  return { sense: obj.sense, coefficients };
}

/**
 * Assemble a full model. Two passes: ground the variable rules into the decision
 * variables (that set is the existence universe), then ground the constraints and
 * objective against it.
 */
export function groundModel(
  variableRules: Rule[],
  constraintRules: Rule[],
  objective: Objective,
  data: Data,
  catalog: Catalog,
): Model {
  const variables = groundVariables(variableRules, data, catalog);
  const exists = existingVariables(variables);
  return {
    variables,
    constraints: groundRules(constraintRules, data, catalog, exists),
    objective: groundObjective(objective, data, catalog, exists),
  };
}
